import type {
  ContractSignMethod,
  ContractSignState,
  DocBlock,
  DocConfidentiality,
  DocDiscipline,
  DocExpiryState,
  DocZone,
  FullTextSource,
  FullTextStatus,
  LeaveForfeitReason,
  LegalKind,
  LegalStatus,
  AdvanceMethod,
  AdvanceStatus,
  ContractState,
  EmploymentContractStatus,
  EmploymentContractType,
  LeavePart,
  LeaveStatus,
  PolicyCategory,
  PolicyScope,
  ProbationLevel,
  ProbationResult,
  ProbationState,
  WorkGroup,
} from '@mt/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { downloadBlob } from '@/lib/utils';
import type { StatusKind } from '@/design-system/status';

/** P21 PR 4 – Cổng nhân viên: hook gọi API /me/* và /hr/* (docs/phases/P21.md §9.7). */
export const ESS_KEY = ['ess'] as const;

export const LEAVE_STATUS_KIND: Record<LeaveStatus, StatusKind> = { pending: 'pending', approved: 'approved', rejected: 'rejected', cancelled: 'cancelled' };
export const ADVANCE_STATUS_KIND: Record<AdvanceStatus, StatusKind> = {
  pending_check: 'pending',
  pending_approval: 'processing',
  approved: 'approved',
  rejected: 'rejected',
  returned: 'draft',
  cancelled: 'cancelled',
};

/** Màu trạng thái thử việc (DEC-242) */
export const PROBATION_STATE_KIND: Record<ProbationState, StatusKind> = {
  upcoming: 'draft',
  running: 'processing',
  ending: 'expired',
  awaiting: 'pending',
  passed: 'confirmed',
  hired: 'completed',
  failed: 'rejected',
  terminated: 'cancelled',
  cancelled: 'cancelled',
};
export const CONTRACT_STATE_KIND: Record<ContractState, StatusKind> = {
  upcoming: 'draft',
  active: 'approved',
  expiring: 'expired',
  expired: 'rejected',
  terminated: 'cancelled',
  cancelled: 'cancelled',
};

/** Số dư phép năm theo công tháng (DEC-244): đầu kỳ + cộng tháng đủ công − đã nghỉ − vắng không phép */
export interface LeaveBalanceView {
  eligible: boolean;
  employmentType: string;
  officialSince: string | null;
  opening: { month: string; days: number; note: string | null; version: number; updatedByName: string | null; updatedAt: string } | null;
  startMonth: string;
  minWork: number;
  perMonth: number;
  /** DEC-811: nghỉ từ N ngày / vắng không phép từ N ngày thì mất phép tháng đó (0 = tắt) */
  forfeitAbsentDays: number;
  forfeitUnexcusedDays: number;
  accrued: number;
  used: number;
  deducted: number;
  expired: number;
  pending: number;
  closing: number;
  available: number;
  thisMonth: { month: string; work: number; accrued: number; eligible: boolean; forfeit: LeaveForfeitReason | null } | null;
  months?: { month: string; eligible: boolean; work: number; used: number; unexcused: number; absent?: number; accrued: number; deducted: number; expired: number; closing: number; forfeit: LeaveForfeitReason | null }[];
}

export interface LeaveBalanceRow extends LeaveBalanceView {
  employeeId: number;
  code: string;
  fullName: string;
  departmentName: string | null;
}

export interface AdvanceContext {
  month: string;
  ratio: number;
  contractSalary: number | null;
  /** Khoản thu nhập cố định / theo công / công thức đang gán, tính đủ tháng. */
  allowances: { code: string; name: string; amount: number }[];
  /** Tổng lương tháng đủ công = lương HĐ + allowances; hạn mức = ratio × số này. */
  monthlyPay: number | null;
  limit: number;
  used: number;
  approved: number;
  remaining: number;
  periodApproved: boolean;
  /** DEC-389: công đã làm tới hôm nay / công chuẩn; lương theo công; tự duyệt khi tổng tạm ứng tháng ≤ autoLimit */
  workDays: number;
  stdDays: number;
  earnedPay: number;
  autoRatio: number;
  autoLimit: number;
  autoRemaining: number;
}

export interface EssSummary {
  employee: { id: number; code: string; fullName: string; departmentName: string | null; workGroup: WorkGroup } | null;
  leave?: LeaveBalanceView;
  advance?: AdvanceContext;
  unreadPolicies?: number;
  /** DEC-249: lệnh được giao chưa xong */
  openWorkOrders?: number;
  /** P21 PR 11: khóa học được giao chưa xong */
  trainingTodo?: number;
  /** P21 PR 11b: phiếu KPI kỳ đang mở chưa tự chấm */
  kpiTodo?: number;
  /** DEC-265: vi phạm còn giải trình được; tháng bảng công chờ NV xác nhận (ngày 1–3, DEC-680) */
  openViolations?: number;
  timesheetToConfirm?: string | null;
  contract?: { contractNo: string; expiresOn: string | null; contractType: EmploymentContractType } | null;
  /** DEC-301: hợp đồng chờ NV ký trên ứng dụng */
  contractsToSign?: number;
  latestPayslipMonth?: string | null;
  recent?: { kind: 'leave' | 'advance'; id: number; title: string; status: string; statusLabel: string; createdAt: string; approverLabel: string }[];
}

export interface LeaveType {
  id: number;
  code: string;
  name: string;
  isPaid: boolean;
  usesAnnualBalance: boolean;
  requiresEvidence: boolean;
  symbol: string;
  sortOrder: number;
  isActive: boolean;
  version: number;
}

export interface LeaveInput {
  leaveTypeId: number;
  fromDate: string;
  toDate: string;
  fromPart: LeavePart;
  toPart: LeavePart;
  reason: string;
  handoverNote?: string | null;
  evidencePhoto?: { data: string; thumb: string } | null;
}

export interface LeavePreview {
  days: number;
  balance: LeaveBalanceView;
  afterLeave: number | null;
  insufficient: boolean;
  /** DEC-247: phép năm tối đa maxAnnualDays ngày / đơn, phần vượt (ngày) thành nghỉ không lương */
  unpaidExtra?: number;
  maxAnnualDays?: number;
  approver: string;
  /** Giờ vào ca ngày bắt đầu nghỉ; gửi trễ hơn (giờ vào ca − N phút) bị chặn – DEC-246 */
  startTime: string;
  tooLate: boolean;
  needsEvidence: boolean;
  minLeadMinutes: number;
  evidenceReason: string;
}

export type AttendanceExcuseKind = 'late' | 'early' | 'dispatch';
export interface AttendanceExcuseView {
  id: number;
  workDate: string;
  kind: AttendanceExcuseKind;
  kindLabel: string;
  reasonCode: string;
  reasonLabel: string;
  note: string;
  expectedAt: string | null;
  photoId: number | null;
  status: LeaveStatus;
  statusLabel: string;
  approverLabel: string;
  decisionReason: string | null;
  createdAt: string;
}

export interface LeaveView {
  id: number;
  employeeId: number;
  employeeCode: string;
  employeeName: string;
  departmentName: string | null;
  leaveTypeId: number;
  leaveTypeName: string;
  fromDate: string;
  toDate: string;
  fromPart: LeavePart;
  toPart: LeavePart;
  days: number;
  isPaid: boolean;
  symbol: string;
  reason: string;
  handoverNote: string | null;
  status: LeaveStatus;
  statusLabel: string;
  approverLabel: string;
  decidedAt: string | null;
  decidedByName: string | null;
  decisionReason: string | null;
  cancelReason: string | null;
  version: number;
  createdAt: string;
}

export interface AdvanceView {
  id: number;
  month: string;
  amount: number;
  reason: string;
  method: AdvanceMethod;
  status: AdvanceStatus;
  statusLabel: string;
  limitAmount: number;
  checkedByName: string | null;
  approvedByName: string | null;
  decidedByName: string | null;
  decisionReason: string | null;
  version: number;
  createdAt: string;
}

export interface RequestRow {
  /** overtime = NV tự khai tăng ca kèm ảnh (DEC-249) */
  kind: 'leave' | 'advance' | 'excuse' | 'overtime' | 'violation';
  key: string;
  id: number;
  version: number;
  employeeId: number;
  employeeCode: string;
  employeeName: string;
  departmentId: number | null;
  departmentName: string | null;
  status: LeaveStatus | AdvanceStatus;
  statusLabel: string;
  reason: string;
  decisionReason: string | null;
  createdAt: string;
  approverLabel: string;
  summary: string;
  typeName?: string;
  isPaid?: boolean;
  fromDate?: string;
  toDate?: string;
  amount?: number;
  month?: string;
  /** Ảnh xác thực (đơn nghỉ sát ngày / nghỉ ốm, xin đi trễ) – DEC-246 */
  photoId?: number | null;
  lat?: number;
  lng?: number;
  actions: ('approve' | 'reject' | 'check' | 'return')[];
  waitingForMe: boolean;
}

export interface RequestFilter {
  kind?: 'leave' | 'advance' | 'excuse' | 'overtime' | 'violation';
  status?: 'mine' | 'pending' | 'approved' | 'rejected' | 'cancelled' | 'all';
  departmentId?: number;
  month?: string;
  q?: string;
}

export interface MySalary {
  employee: EssSummary['employee'];
  profile: { effectiveFrom: string; contractSalary: number; insuranceSalary: number | null; dependants: number; hasInsurance: boolean } | null;
  items: { code: string; name: string; calc: string; amount: number }[];
  driver: { month: string; workDays: number; deliveries: number; gpsKm: number; overtime: number; latePenalty: number; linkedDriver: boolean } | null;
}

export interface ContractView {
  id: number;
  employeeId: number;
  employeeCode: string;
  employeeName: string;
  parentId: number | null;
  parentNo: string | null;
  contractNo: string;
  contractType: EmploymentContractType;
  typeLabel: string;
  signedOn: string | null;
  effectiveFrom: string;
  expiresOn: string | null;
  salary: number | null;
  positionName: string | null;
  content: string | null;
  fileId: number | null;
  fileName: string | null;
  status: EmploymentContractStatus;
  endedOn: string | null;
  statusReason: string | null;
  state: ContractState;
  stateLabel: string;
  daysLeft: number | null;
  version: number;
  updatedByName: string | null;
  updatedAt: string;
  // HĐ thử việc (DEC-242)
  probationLevel: ProbationLevel | null;
  probationLevelLabel: string | null;
  referenceSalary: number | null;
  mentorEmployeeId: number | null;
  mentorName: string | null;
  criteria: string | null;
  probationResult: ProbationResult | null;
  probationResultLabel: string | null;
  resultOn: string | null;
  resultNote: string | null;
  resultByName: string | null;
  probationState: ProbationState | null;
  probationStateLabel: string | null;
  probationDays: number | null;
  salaryRate: number | null;
  sourceContractId: number | null;
  sourceContractNo: string | null;
  nextContractId: number | null;
  nextContractNo: string | null;
  /** DEC-301: lượt ký trên ứng dụng gần nhất (bỏ qua lượt đã hủy nếu có lượt khác) */
  signingId: number | null;
  signingStatus: SigningStatus | null;
  /** DEC-619: HĐLĐ điện tử (NĐ 337/2025, TT 08/2026) – phụ lục dùng mã định danh của HĐ gốc */
  signMethod: ContractSignMethod;
  signMethodLabel: string;
  econtractProvider: string | null;
  econtractId: string | null;
  econtractSignedAt: string | null;
  signState: ContractSignState;
  signStateLabel: string;
}

// --- Ký hợp đồng trên ứng dụng (DEC-301) ---
export type SigningStatus = 'company_pending' | 'employee_pending' | 'signed' | 'cancelled';
export const SIGNING_STATUS_KIND: Record<SigningStatus, StatusKind> = {
  company_pending: 'pending', employee_pending: 'processing', signed: 'completed', cancelled: 'cancelled',
};
/** DEC-619: tình trạng ký HĐLĐ (giấy / nội bộ / eContract) */
export const SIGN_STATE_KIND: Record<ContractSignState, StatusKind> = {
  draft: 'draft', company_pending: 'pending', employee_pending: 'processing', internal: 'confirmed', paper: 'completed', awaiting_id: 'processing', done: 'completed',
};
export interface ContractSigning {
  id: number;
  contractId: number;
  contractNo: string;
  contractType: EmploymentContractType;
  typeLabel: string;
  effectiveFrom: string;
  expiresOn: string | null;
  employeeId: number;
  employeeCode: string;
  employeeName: string;
  departmentName: string | null;
  title: string;
  status: SigningStatus;
  statusLabel: string;
  contentSha256: string;
  sentAt: string;
  sentByName: string | null;
  companySignedAt: string | null;
  companySignerName: string | null;
  employeeViewedAt: string | null;
  employeeSignedAt: string | null;
  fileId: number | null;
  pdfSha256: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  version: number;
}
export interface ContractSigningDetail extends ContractSigning {
  blocks: DocBlock[];
  companySignature: string | null;
  signature: string | null;
}
export const useContractSignings = (status: SigningStatus | 'open', enabled = true) =>
  useQuery({
    queryKey: [...ESS_KEY, 'contract-signings', status],
    queryFn: () => api.get<{ counts: Partial<Record<string, number>>; items: ContractSigning[] }>('/hr/contract-signings', { status }),
    enabled,
  });
export const useContractSigning = (id: number | null) =>
  useQuery({ queryKey: [...ESS_KEY, 'contract-signing', id], queryFn: () => api.get<ContractSigningDetail>(`/hr/contract-signings/${id}`), enabled: id !== null });
export const useMySignings = () => useQuery({ queryKey: [...ESS_KEY, 'my-signings'], queryFn: () => api.get<ContractSigning[]>('/me/contract-signings') });
export const useMySigning = (id: number | null) =>
  useQuery({ queryKey: [...ESS_KEY, 'my-signing', id], queryFn: () => api.get<ContractSigningDetail>(`/me/contract-signings/${id}`), enabled: id !== null });

export interface ProbationView extends ContractView {
  departmentName: string | null;
}
export interface ProbationList {
  today: string;
  warnDays: number;
  counts: Partial<Record<ProbationState, number>>;
  items: ProbationView[];
}

export interface ExpiringContract {
  id: number;
  employeeId: number;
  employeeCode: string;
  employeeName: string;
  departmentName: string | null;
  contractNo: string;
  typeLabel: string;
  expiresOn: string;
  daysLeft: number | null;
  state: ContractState;
}

export interface PdfPayload {
  fileName: string;
  data: string;
}

export interface ContractInput {
  parentId?: number | null;
  contractNo: string;
  contractType: EmploymentContractType;
  signedOn?: string | null;
  effectiveFrom: string;
  expiresOn?: string | null;
  salary?: number | null;
  positionName?: string | null;
  content?: string | null;
  file?: PdfPayload | null;
  probationLevel?: ProbationLevel | null;
  referenceSalary?: number | null;
  mentorEmployeeId?: number | null;
  criteria?: string | null;
  sourceContractId?: number | null;
}

export type DocLinkSource = 'drive' | 'tvpl' | 'web';
export interface MyPolicy {
  id: number;
  docCode: string;
  title: string;
  category: PolicyCategory;
  categoryLabel: string;
  zone: DocZone;
  disciplines: DocDiscipline[];
  disciplineLabels: string[];
  confidentiality: DocConfidentiality;
  legalKind: LegalKind | null;
  legalKindLabel: string | null;
  refNumber: string | null;
  issuer: string | null;
  legalStatus: LegalStatus | null;
  legalStatusLabel: string | null;
  scopeType: PolicyScope;
  scopeLabel: string;
  requireAck: boolean;
  versionId: number;
  versionNo: number;
  effectiveFrom: string;
  hasFile: boolean;
  fullTextStatus: FullTextStatus | null;
  externalUrl: string | null;
  linkSource: DocLinkSource | null;
  acknowledgedAt: string | null;
  previousAckAt: string | null;
  unread: boolean;
}
export type MyPolicyDetail = MyPolicy & { contentMd: string | null; fileName: string | null; changeNote: string | null };

export interface PolicyView {
  id: number;
  docCode: string;
  title: string;
  category: PolicyCategory;
  categoryLabel: string;
  zone: DocZone;
  zoneLabel: string;
  disciplines: DocDiscipline[];
  disciplineLabels: string[];
  confidentiality: DocConfidentiality;
  confidentialityLabel: string;
  viewerUserIds: number[];
  ownerUserId: number | null;
  ownerName: string | null;
  reviewMonths: number | null;
  nextReviewOn: string | null;
  reviewDue: boolean;
  legalKind: LegalKind | null;
  legalKindLabel: string | null;
  refNumber: string | null;
  issuer: string | null;
  issuedOn: string | null;
  expiresOn: string | null;
  expiryState: DocExpiryState;
  expiryDaysLeft: number | null;
  legalStatus: LegalStatus | null;
  legalStatusLabel: string | null;
  replacedById: number | null;
  replacedByCode: string | null;
  replacedByTitle: string | null;
  basisIds: number[];
  basisAlert: boolean;
  scopeType: PolicyScope;
  scopeIds: number[];
  scopeGroups: WorkGroup[];
  scopeLabel: string;
  requireAck: boolean;
  status: 'active' | 'archived';
  currentVersionId: number | null;
  versionNo: number | null;
  effectiveFrom: string | null;
  hasFile: boolean;
  externalUrl: string | null;
  linkSource: DocLinkSource | null;
  version: number;
  updatedAt: string;
  updatedByName: string | null;
  targetCount: number;
  ackCount: number;
  /** P24 DEC-487: toàn văn lưu trong ERP */
  fullTextStatus: FullTextStatus | null;
  fullTextSections: number;
  fullTextError: string | null;
}
export interface PolicyVersionView {
  id: number;
  versionNo: number;
  contentMd: string | null;
  fileId: number | null;
  fileName: string | null;
  externalUrl: string | null;
  effectiveFrom: string;
  changeNote: string | null;
  createdAt: string;
  createdByName: string | null;
  ackCount: number;
}
export interface PolicyRef {
  id: number;
  docCode: string;
  title: string;
}
export type PolicyDetail = PolicyView & {
  versions: PolicyVersionView[];
  basis: (PolicyRef & { refNumber: string | null; legalStatus: LegalStatus | null; legalStatusLabel: string | null })[];
  dependents: PolicyRef[];
};
export interface PolicyInput {
  title: string;
  category: PolicyCategory;
  scopeType: PolicyScope;
  scopeIds: number[];
  scopeGroups: WorkGroup[];
  requireAck: boolean;
  effectiveFrom: string;
  contentMd?: string | null;
  file?: PdfPayload | null;
  keepFile?: boolean;
  changeNote?: string | null;
  externalUrl?: string | null;
  disciplines: DocDiscipline[];
  confidentiality: DocConfidentiality;
  viewerUserIds: number[];
  ownerUserId?: number | null;
  reviewMonths?: number | null;
  legalKind?: LegalKind | null;
  refNumber?: string | null;
  issuer?: string | null;
  issuedOn?: string | null;
  expiresOn?: string | null;
  legalStatus?: LegalStatus | null;
  replacedById?: number | null;
  basisIds: number[];
}
export interface PolicyReaders {
  policyId: number;
  versionId: number;
  versionNo: number;
  total: number;
  read: number;
  readers: { employeeId: number; code: string; fullName: string; departmentName: string | null; hasAccount: boolean; acknowledgedAt: string | null; ip: string | null }[];
}

export interface EssSettingsView {
  settings: { advanceMaxRatio: number; annualLeaveDays: number; seniorityStepYears: number; seniorityBonusDays: number; contractWarnDays: number; leaveAccrualMinWork: number; leaveAccrualDays: number; leaveForfeitAbsentDays: number; leaveForfeitUnexcusedDays: number; leaveMinLeadMinutes: number; leaveEvidenceHours: number; version: number; updatedAt: string; updatedByName: string | null };
  leaveTypes: LeaveType[];
}

// --- Của tôi ---
export const useEssSummary = () => useQuery({ queryKey: [...ESS_KEY, 'me'], queryFn: () => api.get<EssSummary>('/me/ess') });
export const useMyLeaveTypes = () => useQuery({ queryKey: [...ESS_KEY, 'leave-types'], queryFn: () => api.get<LeaveType[]>('/me/leave-types'), staleTime: 300_000 });
export const useMyLeaves = (year?: number) => useQuery({ queryKey: [...ESS_KEY, 'my-leaves', year ?? null], queryFn: () => api.get<LeaveView[]>('/me/leaves', year ? { year } : undefined) });
export const useMyAttendanceExcuses = () => useQuery({ queryKey: [...ESS_KEY, 'my-excuses'], queryFn: () => api.get<AttendanceExcuseView[]>('/me/excuses') });
export const useMyAdvances = () => useQuery({ queryKey: [...ESS_KEY, 'my-advances'], queryFn: () => api.get<AdvanceView[]>('/me/advances') });
export const useAdvanceLimit = (month: string) =>
  useQuery({ queryKey: [...ESS_KEY, 'advance-limit', month], queryFn: () => api.get<AdvanceContext>('/me/advance-limit', { month }), retry: false });
export const useMySalary = () => useQuery({ queryKey: [...ESS_KEY, 'my-salary'], queryFn: () => api.get<MySalary>('/me/salary') });
export const useMyContracts = () => useQuery({ queryKey: [...ESS_KEY, 'my-contracts'], queryFn: () => api.get<ContractView[]>('/me/contracts') });
export const useMyPolicies = () => useQuery({ queryKey: [...ESS_KEY, 'my-policies'], queryFn: () => api.get<MyPolicy[]>('/me/policies') });
export const useMyPolicy = (id: number | null) =>
  useQuery({ queryKey: [...ESS_KEY, 'my-policy', id], queryFn: () => api.get<MyPolicyDetail>(`/me/policies/${id}`), enabled: id !== null });

// --- Duyệt đơn ---
export const useRequests = (f: RequestFilter) =>
  useQuery({
    queryKey: [...ESS_KEY, 'requests', f],
    queryFn: () => api.get<{ items: RequestRow[]; waitingForMe: number }>('/hr/requests', { ...f } as Record<string, string | number | undefined>),
  });
export const useEssSettings = (enabled = true) => useQuery({ queryKey: [...ESS_KEY, 'settings'], queryFn: () => api.get<EssSettingsView>('/hr/ess-settings'), enabled });

// --- Hợp đồng ---
export const useEmployeeContracts = (employeeId: number | null) =>
  useQuery({ queryKey: [...ESS_KEY, 'contracts', employeeId], queryFn: () => api.get<ContractView[]>(`/hr/employees/${employeeId}/contracts`), enabled: employeeId !== null });
export const useExpiringContracts = (enabled = true) =>
  useQuery({ queryKey: [...ESS_KEY, 'contracts-expiring'], queryFn: () => api.get<{ today: string; warnDays: number; items: ExpiringContract[] }>('/hr/contracts/expiring'), enabled });

export const useLeaveBalances = (f: { departmentId?: number; q?: string; scope?: 'official' | 'all' }) =>
  useQuery({
    queryKey: [...ESS_KEY, 'leave-balances', f],
    queryFn: () => api.get<{ month: string; minWork: number; perMonth: number; forfeitAbsentDays: number; forfeitUnexcusedDays: number; items: LeaveBalanceRow[] }>('/hr/leave-balances', { ...f } as Record<string, string | number | undefined>),
  });

export type LaborTab = 'open' | 'expiring' | 'expired' | 'active' | 'upcoming' | 'terminated' | 'all';
export interface LaborContractView extends ContractView {
  departmentName: string | null;
  renewedByNo: string | null;
}
export const useLaborContracts = (f: { tab?: LaborTab; q?: string; departmentId?: number }) =>
  useQuery({
    queryKey: [...ESS_KEY, 'labor-contracts', f],
    queryFn: () => api.get<{ today: string; warnDays: number; counts: Partial<Record<string, number>>; items: LaborContractView[] }>('/hr/labor-contracts', { ...f } as Record<string, string | number | undefined>),
  });

export const useProbations = (f: { state?: ProbationState | 'open'; q?: string; departmentId?: number }) =>
  useQuery({ queryKey: [...ESS_KEY, 'probations', f], queryFn: () => api.get<ProbationList>('/hr/probations', { ...f } as Record<string, string | number | undefined>) });

// --- Tài liệu công ty (nội quy P21 nâng cấp, P24 DEC-459) ---
export interface PolicyFilter {
  zone?: DocZone;
  category?: PolicyCategory;
  discipline?: DocDiscipline;
  scopeType?: PolicyScope;
  status?: 'active' | 'archived';
  attention?: 'review' | 'expiring';
  /** P24 DEC-487: ok = có toàn văn, missing = chưa có / không tải được */
  fullText?: 'ok' | 'missing';
  q?: string;
}
export const usePolicies = (f: PolicyFilter) =>
  useQuery({
    queryKey: [...ESS_KEY, 'policies', f],
    queryFn: () => api.get<PolicyView[]>('/hr/policies', { ...f } as Record<string, string | undefined>),
    // Đang tải toàn văn → làm mới mỗi 15 giây để thấy trạng thái
    refetchInterval: (q) => (q.state.data?.some((p) => p.fullTextStatus === 'queued' || p.fullTextStatus === 'running') ? 15000 : false),
  });
export const usePolicyAttention = () =>
  useQuery({ queryKey: [...ESS_KEY, 'policies-attention'], queryFn: () => api.get<{ review: number; expiring: number }>('/hr/policies-attention') });
export const usePolicy = (id: number | null) =>
  useQuery({ queryKey: [...ESS_KEY, 'policy', id], queryFn: () => api.get<PolicyDetail>(`/hr/policies/${id}`), enabled: id !== null });
export const usePolicyReaders = (id: number | null, versionId?: number) =>
  useQuery({
    queryKey: [...ESS_KEY, 'policy-readers', id, versionId ?? null],
    queryFn: () => api.get<PolicyReaders>(`/hr/policies/${id}/readers`, versionId ? { versionId } : undefined),
    enabled: id !== null,
  });

/** Mutation chung: báo lỗi bằng toast, xong làm mới toàn bộ dữ liệu Cổng NV (+ bảng công vì đơn nghỉ đổi công). */
export function useEssMutation<V, R = unknown>(fn: (v: V) => Promise<R>, ok?: string | ((r: R) => string)) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ESS_KEY });
      void qc.invalidateQueries({ queryKey: ['attendance'] });
      void qc.invalidateQueries({ queryKey: ['work-orders'] });
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      if (ok) toast.success(typeof ok === 'string' ? ok : ok(r));
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Lỗi không xác định'),
  });
}

/** Đọc file PDF (≤ 10MB) thành base64 để gửi kèm JSON. */
export async function readPdf(file: File): Promise<PdfPayload> {
  if (file.type && file.type !== 'application/pdf') throw new Error('Chỉ nhận file PDF');
  if (file.size > 10 * 1024 * 1024) throw new Error('File PDF tối đa 10MB');
  const buf = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return { fileName: file.name, data: btoa(bin) };
}

/** Mở PDF có xác thực (tải blob rồi mở tab mới). */
export async function openPdf(path: string) {
  return openBlobPdf(path);
}

// ---------------------------------------------------------------------------
// P24 DEC-487: toàn văn tài liệu lưu trong ERP
// ---------------------------------------------------------------------------
export interface FullTextSectionView {
  ord: number;
  label: string;
  path: string;
  heading: string;
  body: string;
}
export interface FullTextView {
  policyId: number;
  status: FullTextStatus | null;
  statusLabel: string;
  source: FullTextSource | null;
  sourceLabel: string | null;
  sourceUrl: string | null;
  hasFile: boolean;
  charCount: number;
  sectionCount: number;
  error: string | null;
  triedUrls: string[];
  fetchedAt: string | null;
  sections: FullTextSectionView[];
}
export interface FullTextSummary {
  ok: number;
  failed: number;
  pending: number;
  none: number;
  failedItems: { id: number; docCode: string; title: string; refNumber: string | null; error: string | null }[];
}
/** mine = Cổng NV (/me/policies), còn lại trang Tài liệu công ty. Đang tải thì hỏi lại mỗi 5 giây. */
export const usePolicyFullText = (id: number | null, mine = false) =>
  useQuery({
    queryKey: [...ESS_KEY, 'policy-fulltext', mine, id],
    queryFn: () => api.get<FullTextView>(`${mine ? '/me' : '/hr'}/policies/${id}/fulltext`),
    enabled: id !== null,
    refetchInterval: (q) => (q.state.data?.status === 'queued' || q.state.data?.status === 'running' ? 5000 : false),
  });
export const useFullTextSummary = (enabled: boolean) =>
  useQuery({
    queryKey: [...ESS_KEY, 'fulltext-summary'],
    queryFn: () => api.get<FullTextSummary>('/hr/policies-fulltext'),
    enabled,
    refetchInterval: (q) => ((q.state.data?.pending ?? 0) > 0 ? 15000 : false),
  });

/** File toàn văn tải lên: PDF hoặc Word .docx ≤ 10 MB → base64 */
export async function readDocFile(file: File): Promise<PdfPayload> {
  const name = file.name.toLowerCase();
  if (!name.endsWith('.pdf') && !name.endsWith('.docx')) throw new Error('Chỉ nhận file PDF hoặc Word .docx (file .doc cũ: mở Word, lưu lại thành .docx hoặc PDF)');
  if (file.size > 10 * 1024 * 1024) throw new Error('File tối đa 10MB');
  const buf = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
  return { fileName: file.name, data: btoa(bin) };
}

/** Tải toàn văn (PDF) về máy */
export async function downloadFullText(path: string, fileName: string) {
  try {
    // Tên file không dấu (trình duyệt / Windows không đổi thành "download")
    const slug = fileName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
    downloadBlob(await api.blob(path), `${slug || 'toan-van'}.pdf`);
  } catch (e) {
    toast.error(e instanceof ApiError ? e.message : 'Không tải được file');
  }
}

async function openBlobPdf(path: string) {
  // Mở tab trước (đồng bộ với cú bấm) để trình duyệt điện thoại không chặn cửa sổ bật lên
  const w = window.open('', '_blank');
  try {
    const blob = await api.blob(path);
    const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    if (w) w.location.href = url;
    else window.location.href = url;
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    w?.close();
    toast.error(e instanceof ApiError ? e.message : 'Không mở được file');
  }
}

export const fmtDays = (n: number) => n.toLocaleString('vi-VN', { maximumFractionDigits: 1 });
